import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Paper,
  makeStyles,
  Link,
} from "@material-ui/core";
import { useState } from "react";
import { TextControl, PasswordControl, Form } from "../Utils/Control";
import { login } from "../../Service/UserFactory";
import { Link as RouterLink } from "react-router-dom";
import DocumentTitle from "../DocumentTitle";
import { useForm } from "react-hook-form";
import { EMAIL_REGEX } from "../../assets/data/Other";
import PropTypes from "prop-types";

const useStyles = makeStyles((theme) => ({
  "@global": {
    body: {
      margin: 0,
      padding: 0,
      backgroundColor: "#fff",
    },
  },
  root: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    width: "100vw",
    backgroundColor: "#fff",
    padding: theme.spacing(2),
  },
  card: {
    padding: theme.spacing(4, 5),
    maxWidth: 400,
    width: "100%",
    borderRadius: 14,
    backgroundColor: "#fff",
    textAlign: "center",
    border: "0.3px solid rgba(0, 0, 0, 0.15)", 
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)", 
  },
  welcomeText: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: "1.4rem",
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    color: "#333",
    marginBottom: theme.spacing(3),
    fontSize: "1rem",
  },
  emailField: {
    textAlign: "left",
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: "#fff",
      height: 44,
      "& fieldset": {
        border: "1.2px solid #ccc",
      },
      "&:hover fieldset": {
        borderColor: "#999",
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
    },
  },
  passwordField: {
    marginBottom: theme.spacing(1.5),
    textAlign: "left",
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: "#fff",
      height: 44,
      "& fieldset": {
        border: "1.2px solid #ccc",
      },
      "&:hover fieldset": {
        borderColor: "#999",
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
    },
  },
  label: {
    display: "block",
    marginBottom: theme.spacing(0.5),
    fontWeight: 500,
    fontSize: "0.9rem",
    color: "#333",
  },
  forgotAndButton: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  forgotPassword: {
    fontSize: "0.85rem",
    textDecoration: "none",
    color: theme.palette.primary.main,
    "&:hover": {
      textDecoration: "underline",
    },
  },
  linkText: {
    fontSize: "0.85rem",
    marginTop: theme.spacing(0.4),
    color: theme.palette.primary.main,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  submitBtn: {
    padding: "8px 20px",
    borderRadius: 10,
    fontWeight: 500,
    fontSize: "0.9rem",
    textTransform: "none",
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  discoverSection: {
    // CHANGE 3: Increased gap above the "Discover" section
    marginTop: theme.spacing(4),
  },
  discoverInlineLinks: {
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(3),
  },
  discoverTitle: {
    fontWeight: 400,
    fontSize: "1rem",
    marginBottom: theme.spacing(1),
    color: "#000",
  },
  footer: {
    marginTop: theme.spacing(3),
    textAlign: "center",
    color: "#000",
    fontFamily: "Roboto, sans-serif",
    fontWeight: 400,
    fontSize: "0.8rem",
    lineHeight: "100%",
    letterSpacing: "0%",
  },
  footerLinks: {
    marginTop: theme.spacing(1),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: theme.spacing(0.5),
    flexWrap: "nowrap",
    whiteSpace: "nowrap",
    "& a": {
      color: theme.palette.primary.main,
      textDecoration: "none",
      fontSize: "0.8rem",
      display: "inline",
      "&:hover": {
        textDecoration: "underline",
      },
    },
  },
}));

function Login({ title, homePage }) {
  DocumentTitle(title);
  const classes = useStyles();
  const { handleSubmit, control } = useForm();
  const [isLoading, setLoading] = useState(false);

  const validation = {
    email: {
      pattern: {
        value: EMAIL_REGEX,
        message: "Invalid email address.",
      },
      required: "Email is required.",
    },
    password: { required: "Password is required." },
  };

  const submit = async (data) => {
    if (!isLoading) {
      setLoading(true);
      const status = await login(data);
      setLoading(false);
      if (status) homePage();
    }
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.card}>
        <Typography className={classes.welcomeText}>
          Welcome to Falcon
        </Typography>
        <Typography className={classes.subtitle}>
          Sign in to your account
        </Typography>

        <Form
          control={control}
          rules={validation}
          onSubmit={handleSubmit(submit)}
          autoComplete="off"
        >
          <Box className={classes.emailField}>
            <label htmlFor="email" className={classes.label}>
              Email
            </label>
            <TextControl
              id="email"
              type="email"
              name="email"
              size="small"
              variant="outlined"
              fullWidth
              label=" "
            />
          </Box>

          <Box className={classes.passwordField}>
            <label htmlFor="password" className={classes.label}>
              Password
            </label>
            <PasswordControl
              id="password"
              name="password"
              size="small"
              variant="outlined"
              fullWidth
              label=" "
            />
          </Box>

          <Box className={classes.forgotAndButton}>
            <Box display="flex" flexDirection="column" alignItems="flex-start">
              <RouterLink
                to="/forgotpassword"
                className={classes.forgotPassword}
              >
                Forgot Password?
              </RouterLink>
              <RouterLink to="/signup" className={classes.linkText}>
                Sign up for the demo
              </RouterLink>
            </Box>

            <Button className={classes.submitBtn} type="submit">
              {isLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "Sign In"
              )}
            </Button>
          </Box>
        </Form>

        <Box className={classes.discoverSection}>
          <Typography className={classes.discoverTitle}>Discover</Typography>
          <RouterLink
            to="/whats-new"
            className={classes.linkText}
            style={{ display: "block", marginBottom: 4 }}
          >
            What’s new in Falcon?
          </RouterLink>
          <Box className={classes.discoverInlineLinks}>
            <RouterLink to="/offerings" className={classes.linkText}>
              CNCM Offerings
            </RouterLink>
            <RouterLink to="/blog" className={classes.linkText}>
              Blog
            </RouterLink>
          </Box>
        </Box>
        <Box className={classes.footer}>
          Copyright © 2025 CNCM LLC
          <Box className={classes.footerLinks}>
            <Link component={RouterLink} to="/privacy-policy">
              Privacy Policy
            </Link>
            <span>·</span>
            <Link component={RouterLink} to="/terms">
              Terms of Service
            </Link>
            <span>·</span>
            <Link component={RouterLink} to="/contact">
              Contact
            </Link>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

Login.propTypes = {
  title: PropTypes.string.isRequired,
  homePage: PropTypes.func.isRequired,
};

export default Login;
